import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styles from './EditRoles.module.scss';
import { useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Navbar from './Navbar';

const EditRoles = () => {
  const [roles, setRoles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [newRoleName, setNewRoleName] = useState('');
  const [userRole, setUserRole] = useState(null);
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_URL}/auth/userRole`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUserRole(res.data.role);
      } catch (err) {
        console.error('Failed to fetch user role', err);
      }
    };

    fetchUserRole();
  }, [token]);

  useEffect(() => {
    if (userRole === 'admin') {
      const fetchRolesAndGroups = async () => {
        try {
          const [rolesRes, groupsRes] = await Promise.all([
            axios.get(`${process.env.REACT_APP_URL}/roles`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            axios.get(`${process.env.REACT_APP_URL}/groups`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
          ]);
          setRoles(rolesRes.data);
          setGroups(groupsRes.data);
        } catch (err) {
          console.error('Failed to fetch roles or groups', err);
        }
      };

      fetchRolesAndGroups();
    }
  }, [token, userRole]);

  useEffect(() => {
    if (selectedRole && userRole === 'admin') {
      const fetchPermissions = async () => {
        try {
          const res = await axios.get(`${process.env.REACT_APP_URL}/roles/${selectedRole.id}/permissions`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setPermissions(res.data);
        } catch (err) {
          console.error('Failed to fetch permissions', err);
        }
      };

      fetchPermissions();
    }
  }, [selectedRole, token, userRole]);

  const handleCreateRole = async (e) => {
    e.preventDefault();
    if (userRole !== 'admin') return;
    try {
      const res = await axios.post(`${process.env.REACT_APP_URL}/roles`, { name: newRoleName }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRoles([...roles, res.data]);
      setNewRoleName('');
    } catch (err) {
      console.error('Failed to create role', err);
    }
  };

  const handleSetPermission = async (groupId, canRead, canWrite) => {
    if (userRole !== 'admin') return;
    try {
      const res = await axios.post(`${process.env.REACT_APP_URL}/roles/${selectedRole.id}/permissions`, {
        groupId,
        canRead,
        canWrite,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPermissions((prevPermissions) =>
        prevPermissions.map((p) =>
          p.groupId === groupId
            ? { ...p, canRead: res.data.canRead, canWrite: res.data.canWrite }
            : p
        )
      );
    } catch (err) {
      console.error('Failed to set permission', err);
    }
  };

  if (userRole !== 'admin') {
    return <p>You do not have permission to edit roles.</p>;
  }

  return (
    <div className="App">
      <Navbar />
      <div className="allContent">
        <Container fluid>
          <Row className="justify-content-center">
            <Col xs="12" lg="7" className='mt-1'>
              <div className={styles.editRoles}>
                <div className={styles.headerContainer}>
                  <h2>Edit Roles</h2>
                  <form onSubmit={handleCreateRole}>
                    <input
                      type="text"
                      value={newRoleName}
                      onChange={(e) => setNewRoleName(e.target.value)}
                      placeholder="Role Name"
                      required
                    />
                    <button type="submit">Create Role</button>
                  </form>
                </div>
                <p>Manage your team and their permissions here.</p>
                <div className={styles.rolesList}>
                  <table className={styles.rolesTable}>
                    <thead>
                      <tr>
                        <th>Roles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roles.map((role) => (
                        <React.Fragment key={role.id}>
                          <tr
                            className={selectedRole && role.id === selectedRole.id ? styles.selectedRole : ''}
                            onClick={() => setSelectedRole(selectedRole && selectedRole.id === role.id ? null : role)}
                          >
                            <td>{role.name}</td>
                          </tr>
                          {selectedRole && role.id === selectedRole.id && (
                            <tr className={styles.permissionsRow}>
                              <td colSpan="1">
                                <table className={styles.permissionsTable}>
                                  <thead>
                                    <tr>
                                      <th>Group</th>
                                      <th>Read</th>
                                      <th>Write</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {groups.map((group) => {
                                      const permission = permissions.find((p) => p.groupId === group.id) || {};
                                      return (
                                        <tr key={group.id}>
                                          <td>{group.name}</td>
                                          <td>
                                            <input
                                              type="checkbox"
                                              checked={!!permission.canRead}
                                              onChange={(e) =>
                                                handleSetPermission(group.id, e.target.checked, permission.canWrite)
                                              }
                                            />
                                          </td>
                                          <td>
                                            {permission.canRead && (
                                              <input
                                                type="checkbox"
                                                checked={!!permission.canWrite}
                                                onChange={(e) =>
                                                  handleSetPermission(group.id, permission.canRead, e.target.checked)
                                                }
                                              />
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
};

export default EditRoles;
